import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useViewer } from '@/entities/viewer';
import {
  type ActivityEvent,
  EventText,
  useCardHistory,
  useCardVersions,
  type VersionSummary,
} from '@/entities/history';
import { formatDateTime } from '@/shared/lib/datetime';
import { describedFrom, useJournalSearch } from '@/shared/lib/journal-search';
import { Button, ErrorState, LoadingState, SearchField } from '@/shared/ui';

import styles from './CardHistory.module.css';
import { VersionPreview } from './VersionPreview';

interface Props {
  cardId: string;
  boardId: string;
  /** The card's current concurrency token (a restore must be based on it). */
  cardVersion: number;
  onRestored: () => void;
}

type Mode = 'changes' | 'versions';

function useWhen() {
  const { i18n } = useTranslation();
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  return (iso: string) => formatDateTime(iso, i18n.language, zone);
}

/**
 * The "History" section of the card dialog: what happened to the card (Changes) and the saved
 * states it can be restored to (Versions). Both are newest first and load older pages on demand.
 * Opening a state shows a read-only preview with "Restore"; restoring never touches the other
 * history.
 */
export function CardHistory({ cardId, boardId, cardVersion, onRestored }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('changes');
  const [preview, setPreview] = useState<string | null>(null);

  if (preview) {
    return (
      <VersionPreview
        cardId={cardId}
        boardId={boardId}
        versionId={preview}
        cardVersion={cardVersion}
        onBack={() => {
          setPreview(null);
        }}
        onRestored={onRestored}
      />
    );
  }
  return (
    <section className={styles.section} aria-label={t('history.tab')}>
      <div className={styles.modes} role="group" aria-label={t('history.tab')}>
        {(['changes', 'versions'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={`${styles.mode} ${mode === value ? styles.modeOn : ''}`}
            aria-pressed={mode === value}
            onClick={() => {
              setMode(value);
            }}
          >
            {t(`history.${value}`)}
          </button>
        ))}
      </div>
      {mode === 'changes' ? (
        <Changes cardId={cardId} onPreview={setPreview} />
      ) : (
        <Versions cardId={cardId} onPreview={setPreview} />
      )}
    </section>
  );
}

function MoreButton({
  hasNext,
  loading,
  onMore,
}: {
  hasNext: boolean;
  loading: boolean;
  onMore: () => void;
}) {
  const { t } = useTranslation();
  if (!hasNext) return null;
  return (
    <Button disabled={loading} onClick={onMore}>
      {loading ? t('history.loadingMore') : t('history.more')}
    </Button>
  );
}

function Changes({ cardId, onPreview }: { cardId: string; onPreview: (id: string) => void }) {
  const { t } = useTranslation();
  const when = useWhen();
  const catalog = useMemo(() => describedFrom(t('history.events', { returnObjects: true })), [t]);
  const search = useJournalSearch(catalog);
  const history = useCardHistory(cardId, { q: search.q, types: search.types });
  const events: ActivityEvent[] = history.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <>
      <SearchField
        value={search.input}
        onChange={search.setInput}
        label={t('journal.search')}
        placeholder={t('journal.placeholder')}
        clearLabel={t('journal.clear')}
        testId="card-history-search"
      />
      {history.isPending && <LoadingState />}
      {history.isError && (
        <ErrorState error={history.error} onRetry={() => void history.refetch()} />
      )}
      {history.data && events.length === 0 && (
        <p className={styles.empty} data-testid="card-history-empty">
          {search.searching
            ? t('journal.noMatches', { q: search.input.trim() })
            : t('history.empty')}
        </p>
      )}
      {events.length > 0 && (
        <ol className={styles.list} data-testid="card-history">
          {events.map((event) => {
            const versionId =
              typeof event.payload.version_id === 'string' ? event.payload.version_id : null;
            return (
              <li key={event.id} className={styles.item}>
                <p className={styles.text}>
                  <EventText event={event} />
                </p>
                <div className={styles.meta}>
                  <time dateTime={event.created_at}>{when(event.created_at)}</time>
                  {versionId && (
                    <button
                      type="button"
                      className={styles.link}
                      onClick={() => {
                        onPreview(versionId);
                      }}
                    >
                      {t('history.preview')}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <MoreButton
        hasNext={history.hasNextPage}
        loading={history.isFetchingNextPage}
        onMore={() => void history.fetchNextPage()}
      />
    </>
  );
}

function causeLabel(t: (key: string) => string, cause: string): string {
  const key =
    cause === 'baseline' ? 'history.cause.baseline' : `history.cause.${cause.replace('.', '_')}`;
  const label = t(key);
  return label === key ? cause : label;
}

function Versions({ cardId, onPreview }: { cardId: string; onPreview: (id: string) => void }) {
  const { t } = useTranslation();
  const when = useWhen();
  const versions = useCardVersions(cardId);
  if (versions.isPending) return <LoadingState />;
  if (versions.isError) {
    return <ErrorState error={versions.error} onRetry={() => void versions.refetch()} />;
  }
  const items: VersionSummary[] = versions.data.pages.flatMap((page) => page.items);
  if (items.length === 0) return <p className={styles.empty}>{t('history.empty')}</p>;
  const translate = t as unknown as (key: string) => string;
  return (
    <>
      <ol className={styles.list} data-testid="card-versions">
        {items.map((version, index) => (
          <li key={version.id} className={styles.item}>
            <p className={styles.text}>
              <strong>{t('history.versionLabel', { number: version.version_number })}</strong>
              {' · '}
              {causeLabel(translate, version.cause)}
              {index === 0 && <span className={styles.badge}>{t('history.current')}</span>}
            </p>
            <div className={styles.meta}>
              <span>{version.created_by.name ?? t('history.unknownActor')}</span>
              <time dateTime={version.created_at}>{when(version.created_at)}</time>
              <button
                type="button"
                className={styles.link}
                onClick={() => {
                  onPreview(version.id);
                }}
              >
                {t('history.preview')}
              </button>
            </div>
          </li>
        ))}
      </ol>
      <MoreButton
        hasNext={versions.hasNextPage}
        loading={versions.isFetchingNextPage}
        onMore={() => void versions.fetchNextPage()}
      />
    </>
  );
}
